import { useState } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Form,
  FormGroup,
  FormSection,
  LanguageVariants,
  RichText,
  Select,
  Switch,
  Text
} from 'components/common/form';

import type { UpdateUserCustomAttributesFormReq } from 'shared-types/requests';
import type { GetUserCustomAttributesRes } from 'shared-types/responses';

interface Props {
  updateData: () => Promise<void>;
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesFormSettings = ({
  updateData,
  userCustomAttributes
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { userCustomAttributesForm },
    theme
  } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const data: UpdateUserCustomAttributesFormReq = {
        attributes: Object.keys(values.attributes).reduce(
          (attrs, attributeId) => {
            attrs[attributeId] = values.attributes[attributeId];
            return attrs;
          },
          {}
        ),
        userCustomAttributesForm: values.userCustomAttributesForm
      };

      await api.post('/admin/user-custom-attributes-form', data);

      await updateData();

      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );

      setIsSubmitting(false);
    } catch (err) {
      setIsSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const enabledUserCustomAttributes = userCustomAttributes.filter(
    (attribute) => attribute.isEnabled
  );

  const attributes = {};
  enabledUserCustomAttributes.forEach((attribute) => {
    attributes[attribute.id] = {
      userAttributesFormSettings: {
        alwaysAllowSettingUnsetValue:
          attribute.userAttributesFormSettings?.alwaysAllowSettingUnsetValue,
        defaultValue: attribute.userAttributesFormSettings?.defaultValue,
        hideIfSet: Boolean(attribute.userAttributesFormSettings?.hideIfSet),
        isEnabled: Boolean(attribute.userAttributesFormSettings?.isEnabled)
      }
    };
  });

  const initialValues = {
    attributes,
    userCustomAttributesForm
  };

  return (
    <div className="settings">
      <Box padding={0} title={messages.userAttributesForm}>
        <Form
          allowEmptyStrings={true}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <div>
              <FormSection maxWidth="100%">
                <div className="row-md">
                  <div className="col-5of12">
                    <FormGroup label={messages.commonEnabled}>
                      <Switch
                        field="userCustomAttributesForm.isEnabled"
                        id="user-attributes-form-settings-enabled"
                        label={messages.commonEnabled}
                      />
                    </FormGroup>

                    <FormGroup label={messages.commonOptions}>
                      <Checkbox
                        field="userCustomAttributesForm.onlySelfRegistered"
                        id="user-attributes-form-settings-self-registered"
                        label="Show only for self registered users"
                      />
                    </FormGroup>
                  </div>

                  <div className="col">
                    <FormGroup label={messages.commonDescription}>
                      <RichText
                        field="userCustomAttributesForm.description"
                        id="user-attributes-form-settings-description"
                        maxLength={3000}
                      />

                      <LanguageVariants
                        field="userCustomAttributesForm.descriptionTranslations"
                        type="richtext"
                      />
                    </FormGroup>
                  </div>
                </div>

                <FormGroup label={messages.commonAttributes} maxWidth="100%">
                  <div className="attributes">
                    {enabledUserCustomAttributes.map((attribute) => (
                      <div className="attribute" key={attribute.id}>
                        <div className="row-md row-center">
                          <div className="col-1of3">
                            <Switch
                              field={`attributes.${attribute.id}.userAttributesFormSettings.isEnabled`}
                              id={`user-attributes-form-settings-attributes-${attribute.id}-is-enabled`}
                              label={attribute.title}
                            />
                          </div>

                          <div className="col-1of6">
                            <Checkbox
                              field={`attributes.${attribute.id}.userAttributesFormSettings.hideIfSet`}
                              id={`user-attributes-form-settings-attributes-${attribute.id}-hide-if-set`}
                              label={'Hide if set'}
                              labelTooltip={
                                'Hide attribute if value is already set'
                              }
                            />
                          </div>

                          <div className="col-1of6">
                            <Checkbox
                              field={`attributes.${attribute.id}.userAttributesFormSettings.alwaysAllowSettingUnsetValue`}
                              id={`user-attributes-form-settings-attributes-${attribute.id}-always-allow-setting-unset-value`}
                              label={'Allow setting unset value'}
                              labelTooltip={
                                'Always allow setting unset value even if editing is not otherwise allowed'
                              }
                            />
                          </div>

                          <div className="col">
                            <div className="default">
                              <div className="default-label">
                                {messages.commonDefaultValue}
                              </div>
                              <div className="default-input">
                                {attribute.type === 'Boolean' ? (
                                  <Select
                                    field={`attributes.${attribute.id}.userAttributesFormSettings.defaultValue`}
                                    id={`user-attributes-form-settings-attributes-${attribute.id}-default-value-boolean`}
                                    options={[
                                      {
                                        label: 'No default value',
                                        value: ''
                                      },
                                      {
                                        label: messages.commonYes,
                                        value: true
                                      },
                                      {
                                        label: messages.commonNo,
                                        value: false
                                      }
                                    ]}
                                  />
                                ) : attribute.type === 'Number' ? (
                                  <Select
                                    field={`attributes.${attribute.id}.userAttributesFormSettings.defaultValue`}
                                    id={`user-attributes-form-settings-attributes-${attribute.id}-default-value-number`}
                                    isSearchable={false}
                                    options={[
                                      { label: 'No default value', value: '' },
                                      ...Array.from(
                                        Array(
                                          Math.max(
                                            attribute.max - attribute.min + 1,
                                            1
                                          )
                                        ),
                                        (_, i) => ({
                                          label: i + attribute.min,
                                          value: i + attribute.min
                                        })
                                      )
                                    ]}
                                  />
                                ) : attribute.type === 'Array' ||
                                  attribute.type === 'ArrayWithOptions' ? (
                                  <Select
                                    field={`attributes.${attribute.id}.userAttributesFormSettings.defaultValue`}
                                    id={`user-attributes-form-settings-attributes-${attribute.id}-default-value-array`}
                                    creatable={true}
                                    isMulti={true}
                                    isSearchable={true}
                                  />
                                ) : (
                                  <Text
                                    field={`attributes.${attribute.id}.userAttributesFormSettings.defaultValue`}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormGroup>
              </FormSection>

              <FormSection>
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={isSubmitting}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              </FormSection>
            </div>
          )}
        </Form>
      </Box>

      <style jsx>{`
        .attributes {
          margin: 10px 0 -25px;
        }
        .attribute {
          border-top: 1px solid ${theme.lightGrey};
          padding: 10px 0;
        }
        .default {
          align-items: center;
          display: flex;
          gap: 15px;
        }
        .default-label {
          font-size: 1.5rem;
          font-weight: 400;
        }
        .default-input {
          flex-grow: 1;
        }
      `}</style>
    </div>
  );
};

export default UserAttributesFormSettings;
